import React from 'react';
import {
  Puzzle,
  ArrowUpCircle,
  Lock,
  ShieldAlert,
  Network,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { Badge } from './Badge';

/**
 * AccessGate
 *
 * A single, consistent screen for every "you can't be here" state in the app.
 * Previously these were four separate components inside ProtectedRoute (plus
 * UnauthorizedPage), each with its own wording and — worse — its own visual
 * language. Because a blocked user could not tell *why* they were blocked, the
 * fix is one layout that always answers three questions:
 *
 *   1. What is blocking me?   (title + description)
 *   2. Can I fix it myself?   (primary action, only when we can point somewhere)
 *   3. What now?              (hint + back)
 *
 * The gate is purely presentational: callers pass facts and callbacks, never JSX
 * for the frame. Keep the copy here so all gates stay in sync.
 */
export type AccessGateReason =
  | 'module' // a feature module is disabled for this org
  | 'tier' // the org's plan is too low
  | 'permission' // the viewer's role lacks a capability
  | 'platform-admin' // platform staff only
  | 'org-required'; // no active organization selected

export interface AccessGateProps {
  reason: AccessGateReason;
  /** The feature/module this gate protects, when known (e.g. "CHAT"). */
  subject?: string;
  /** The viewer's effective role, shown for permission-style gates. */
  role?: string;
  /**
   * Optional, more specific requirement copy for permission/platform gates.
   * Lets a caller state the exact role needed (e.g. "Super Admin") without
   * forking the gate's layout. Falls back to the generic sentence when omitted.
   */
  requirement?: string;
  /** The org's current plan, shown for tier gates. */
  currentTier?: string;
  /** Plans that would satisfy the gate; the lowest one is highlighted. */
  requiredTiers?: readonly string[];
  /** Whether the viewer can resolve this themselves (e.g. an org admin). */
  canResolve?: boolean;
  /** Overrides the primary button label. Defaults per reason. */
  resolveLabel?: string;
  /** Return to the previous screen. Button is hidden when omitted. */
  onBack?: () => void;
  /** Navigate to where the viewer can resolve the gate. */
  onResolve?: () => void;
  className?: string;
}

const TIER_ORDER = ['LOCAL', 'FOUNDATION', 'MOMENTUM', 'INSTITUTIONAL'];

const REASON_META: Record<
  AccessGateReason,
  { icon: React.ElementType; title: string; iconClass: string; defaultResolveLabel?: string }
> = {
  module: {
    icon: Puzzle,
    title: 'Module Not Enabled',
    iconClass: 'bg-[#FFD600] text-black',
    defaultResolveLabel: 'Enable Module',
  },
  tier: {
    icon: ArrowUpCircle,
    title: 'Plan Upgrade Required',
    iconClass: 'bg-[#FFD600] text-black',
    defaultResolveLabel: 'View Plans',
  },
  permission: {
    icon: Lock,
    title: 'Access Restricted',
    iconClass: 'bg-[#FF0000] text-white',
  },
  'platform-admin': {
    icon: ShieldAlert,
    title: 'Administrator Access Required',
    iconClass: 'bg-[#FF0000] text-white',
  },
  'org-required': {
    icon: Network,
    title: 'Organization Required',
    iconClass: 'bg-[#0044FF] text-white',
    defaultResolveLabel: 'Go to Dashboard',
  },
};

const RoleDetail: React.FC<{ role?: string }> = ({ role }) =>
  role ? (
    <div className="flex items-center justify-center gap-2 mb-6">
      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
        Your role
      </span>
      <Badge variant="gray">{role}</Badge>
    </div>
  ) : null;

const TierDetail: React.FC<{ currentTier?: string; required?: string | null }> = ({
  currentTier,
  required,
}) => {
  if (!required) return null;
  return (
    <div className="flex items-center justify-center gap-3 mb-6">
      <div className="flex flex-col items-center px-4 py-3 bg-[#F5F5F0] dark:bg-slate-900 border-2 border-black dark:border-white min-w-[104px]">
        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
          Current
        </span>
        <span className="text-xs font-bold text-black dark:text-white">{currentTier || 'LOCAL'}</span>
      </div>
      <span className="text-black dark:text-white font-black" aria-hidden="true">
        →
      </span>
      <div className="flex flex-col items-center px-4 py-3 bg-[#FFD600] border-2 border-black min-w-[104px]">
        <span className="text-[10px] font-mono uppercase tracking-widest text-black/60 mb-1">
          Required
        </span>
        <span className="text-xs font-bold text-black">{required}</span>
      </div>
    </div>
  );
};

export const AccessGate: React.FC<AccessGateProps> = ({
  reason,
  subject,
  role,
  requirement,
  currentTier,
  requiredTiers,
  canResolve = false,
  resolveLabel,
  onBack,
  onResolve,
  className,
}) => {
  const meta = REASON_META[reason];

  const lowestRequired = React.useMemo(() => {
    if (!requiredTiers || requiredTiers.length === 0) return null;
    const rank = (tier: string) => {
      const idx = TIER_ORDER.indexOf(tier);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...requiredTiers].sort((a, b) => rank(a) - rank(b))[0];
  }, [requiredTiers]);

  const description = React.useMemo(() => {
    switch (reason) {
      case 'module':
        return (
          <>
            The{' '}
            <strong className="font-bold text-black dark:text-white">{subject || 'requested'}</strong>{' '}
            module is turned off for this organization.
          </>
        );
      case 'tier':
        return (
          <>
            This feature needs the{' '}
            <strong className="font-bold text-black dark:text-white">
              {lowestRequired || 'required'}
            </strong>{' '}
            plan or higher.
          </>
        );
      case 'permission':
        return requirement ? (
          <>{requirement}</>
        ) : (
          <>Your role doesn't include permission to view this area.</>
        );
      case 'platform-admin':
        return requirement ? (
          <>{requirement}</>
        ) : (
          <>This area is limited to platform administrators.</>
        );
      case 'org-required':
        return (
          <>
            Pick an organization before opening this tool. Tools and modules are scoped to the
            organization you're viewing.
          </>
        );
    }
  }, [reason, subject, lowestRequired, requirement]);

  const hint = React.useMemo(() => {
    switch (reason) {
      case 'module':
        return canResolve
          ? 'Turn it on in your organization settings.'
          : 'Ask an administrator to turn it on.';
      case 'tier':
        return canResolve
          ? 'Upgrade the plan to unlock this and other premium capabilities.'
          : 'Ask an administrator to upgrade your organization.';
      case 'permission':
        return 'Ask an administrator if you believe you should have access.';
      case 'platform-admin':
        return 'Sign in with a platform administrator account to continue.';
      case 'org-required':
        return 'Switch organizations from the menu in the top bar.';
    }
  }, [reason, canResolve]);

  const resolvedLabel = resolveLabel || meta.defaultResolveLabel;

  return (
    <div
      className={cn('h-full flex items-center justify-center p-6', className)}
      data-access-gate={reason}
    >
      <div className="max-w-lg w-full bg-white border-4 border-black shadow-[8px_8px_0px_#000000] p-8 text-center rounded-none dark:bg-black dark:border-white dark:shadow-[8px_8px_0px_#FFFFFF]">
        <div
          className={cn(
            'w-16 h-16 border-4 border-black flex items-center justify-center mx-auto mb-6',
            meta.iconClass
          )}
        >
          <meta.icon size={32} />
        </div>

        <h2 className="text-2xl font-black font-display uppercase tracking-tight text-black dark:text-white mb-3">
          {meta.title}
        </h2>

        <p className="text-sm font-mono text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-6 leading-relaxed">
          {description}
        </p>

        {reason === 'tier' ? (
          <TierDetail currentTier={currentTier} required={lowestRequired} />
        ) : (
          <RoleDetail role={reason === 'permission' || reason === 'platform-admin' ? role : undefined} />
        )}

        {(onResolve || onBack) && (
          <div className="flex flex-col gap-3">
            {onResolve && resolvedLabel && (
              <Button variant="primary" className="w-full" onClick={onResolve}>
                {resolvedLabel}
              </Button>
            )}
            {onBack && (
              <Button onClick={onBack} variant="secondary" className="w-full">
                <ArrowLeft size={14} className="mr-1.5" />
                Go Back
              </Button>
            )}
          </div>
        )}

        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mt-6">{hint}</p>
      </div>
    </div>
  );
};
